import * as React from 'react'
import { RadialGauge, useAlert, useEffectWithPolling } from 'uxp/components'
import { IUIPropertyDefinitionMap, IWidgetTemplateInstanceProps, IWidgetTemplateProps, registerCardTemplate } from '../../../common/common'
import { generateIconProp } from '../../../common/icon-selector/IconEditor'
import { SelectableItem, selectUIProps } from '../../../common/SelectableItem'
import { ContainerProperty, extractContainerProperties } from '../../../common/ui-props/ContainerProperty'
import { extractTextProperties, TextProperty } from "../../../common/ui-props/TextProperty";
import { GuageProperty, IGaugeProps } from './GuageProperty'
import { DataSourceProperty } from "../../../common/ui-props/DataSourceProperty";
import { WidgetWrapperWithSidebar } from '../../../common/widget-wrappers/WidgetWrapperWithSidebar'
import { IContextData, useInitialContextData } from '../../../../hooks/useContextData'
import WDFilterPanel from '../../../common/components/WDFilterPanel'
import './styles.scss'
import { executeAndBind, processInitialSource } from '../../../common/data-source-picker/datasource'
import { convertColor, hasValue, parseValue } from '../../../../util'



const XPR: React.ForwardRefExoticComponent<IWidgetTemplateProps & React.RefAttributes<IWidgetTemplateInstanceProps>> = React.forwardRef((props, ref) => {

    const { designer, isInnerField } = props;
    let uiProps = props?.uiProps || {};

    const [loading, setLoading] = React.useState(false)
    let [contextData, setContextData] = React.useState<IContextData>({})
    const [value, setValue] = React.useState(0);
    const [initialSource, setIntitialSource] = React.useState(props.initialSource);

    const alerts = useAlert()
    const containerRef = React.useRef(null)

    const { isLoading, initialContextData } = useInitialContextData(props, [uiProps?.source])


    let defaults: IGaugeProps = {
        min: 0,
        max: 100,
        label: '',
        legend: true,
        gradient: true,
        colors: [
            { color: 'blue', stopAt: 25 },
            { color: 'green', stopAt: 50 },
            { color: 'yellow', stopAt: 75 },
            { color: 'red', stopAt: 100 },
        ],
        refreshInterval: 5000,
        channel: '',
        thickness: 30,
        largeTick: 4,
        smallTick: 1,
        tickColor: 'black',
        backgroundColor: 'white',
        labelColor: '',
        needleColor: '',
        field: '',
    }

    const XPRProps: IUIPropertyDefinitionMap = {
        "headerContainer": ContainerProperty({
            background: {
                color: "",
                image: {
                    image: "",
                    position: "",
                    size: "",
                },
            }
        }),
        'title': TextProperty({ text: '', color: '', size: '' }, 'Title'),
        'source': DataSourceProperty('Source', uiProps.source, uiProps.datasources, props.uxpContext, props.designer, 'dictionary'),
        'chartProperty': GuageProperty('Gauge', uiProps.chartProperty || defaults)
    }

    let { styles: headerStyles } = extractContainerProperties(uiProps.headerContainer)
    let { text: title, styles: titleStyles } = extractTextProperties(uiProps.title, 'Gauge')

    let { min, max, label, legend, colors, refreshInterval: refreshInsterval, channel, thickness, largeTick, smallTick, backgroundColor, tickColor, labelColor, needleColor, gradient } = uiProps.chartProperty as IGaugeProps || defaults

    React.useImperativeHandle(ref, () => ({
        beforeSave: validateBeforeSave,
        onPreview: validateBeforeSave
    }))



    React.useEffect(() => {
        setLoading(isLoading)
        setContextData(prev => ({ ...prev, ...initialContextData }))
    }, [isLoading, initialContextData])

    React.useEffect(() => {
        return () => {
            designer?.removeErrors?.(['datasource', 'field'])
        }
    }, [])

    React.useEffect(() => {
        validateBeforeSave()
    }, [uiProps])


    React.useEffect(() => {

        processInitialSource(props.designer, props.uxpContext, props.uiProps, initialSource, 'dictionary')
            .then(res => {
                if (res) props.designer?.setUIProps(res)
            })
            .catch(e => {
                console.log('exception ', e);

            })

    }, [initialSource]);

    useEffectWithPolling(props.uxpContext, channel || "gauge/1", refreshInsterval || 60000, async () => {
        if (designer?.mode == 'preview' || designer?.mode == 'thumbnail') {
            return;
        }
        if (props.designer && (props.designer?.mode == 'preview' || props.designer?.mode == 'thumbnail')) {
            let _val = value
            if (!_val) _val = min + (max - min) * Math.random()
            else _val = _val + (5 * (Math.random() < 0.5 ? -1 : 1))
            setValue(_val);
            return;
        }

        let params: any = { filters: contextData?.filters || {} }
        executeAndBind(props.uxpContext, uiProps.source, uiProps.datasources, params, contextData)
            .then(d => {
                let val = d?.[uiProps.chartProperty?.field || ''];
                console.log('value ', d, uiProps.chartProperty?.field, val);

                setValue(Number(val) || 0)
            })
    }, [props.designer, contextData, uiProps]);

    // this will be called before save
    function validateBeforeSave() {
        if (!isDataSourceValid(true)) return false
        if (!hasFieldConfigured(true)) return false
        return true
    }

    function isDataSourceValid(showError?: boolean) {
        if (!hasValue(uiProps?.source) || !hasValue(uiProps.source?.source) || !hasValue(uiProps.source?.path)) {

            if (showError) {
                designer?.addErrors?.([{
                    id: 'datasource',
                    type: 'property',
                    error: 'Data source is required',
                    containerId: 'chart-container',
                    fieldId: 'source'
                }])
            }

            return false
        }
        designer?.removeErrors?.(['datasource'])
        return true
    }

    function hasFieldConfigured(showError?: boolean) {
        if (!hasValue(uiProps?.chartProperty?.field)) {
            if (showError) {
                designer?.addErrors?.([{
                    id: 'field',
                    type: 'property',
                    error: 'Field is required',
                    containerId: 'chart-container',
                    fieldId: 'field'
                }])
            }
            return false
        }
        designer?.removeErrors?.(['field'])
        return true
    }
    

    return <WidgetWrapperWithSidebar
        isInnerField={isInnerField}
        containerRef={containerRef}
        designer={props.designer}
        contextData={contextData}
        propDef={XPRProps}
        uiProps={uiProps}
        className="uxp-custom-gauge-template"
        filterSection={() => (props.designer?.mode == 'preview' || (Number(uiProps?.filter?.fields?.length) > 0)) ? <WDFilterPanel
            designer={props.designer}
            uxpContext={props.uxpContext}
            uiProps={uiProps}
            containerRef={containerRef}
            data={contextData || {}}
            onChangeFilters={(data) => {
                setContextData(prev => ({ ...prev, filters: data?.filters || {} }))
            }}
            onClearFilters={() => {
                setContextData(prev => ({ ...prev, filters: {} }))
            }}
            onOpenPanel={() => {
                // if (props.designer && (props.designer.mode == 'preview' || props.designer.mode == 'thumbnail')) {
                //     setDisableDrop(true)
                // }
            }}
        // onClosePanel={() => { setDisableDrop(false) }}
        /> : null}
    >
        <div className="gauge-widget-content">
            <SelectableItem
                designer={props.designer}
                props={selectUIProps(uiProps, XPRProps, 'source', 'chartProperty')}
                elementId='chart-container'
                contextData={contextData}
            >
                <RadialGauge
                    value={value}
                    min={min || 0}
                    max={max || 100}
                    legend={legend}
                    label={() => <>{label}</>}
                    className="wd-custom-guage"
                    colors={colors.map((c)=>({...c,color:convertColor(c.color,contextData)}))}
                    thickness={thickness}
                    largeTick={largeTick}
                    smallTick={smallTick}
                    backgroundColor={convertColor(backgroundColor,contextData)}
                    tickColor={convertColor(tickColor,contextData)}
                    labelColor={convertColor(labelColor,contextData)}
                    needleColor={convertColor(needleColor,contextData)}
                    gradient={gradient}
                />
            </SelectableItem>

        </div>

    </WidgetWrapperWithSidebar>
})

registerCardTemplate({
    id: 'radial-gauge-2',
    name: 'Radial Gauge ',
    description: 'A dynamic radial gauge that displays a single value pushed from Lucy. Your data source needs to provide a single value. Data can be updated by either pushing data into a specific channel from Lucy or by configuring this widget to regularly poll for new updates.',
    template: XPR,
    icon: generateIconProp('tachometerAlt'),
    complexity: 'easy',
    preLoader: 'gauge',
    layout: {
        w: 8,
        h: 10
    },
    expectedSchema: 'dictionary',
    canDragDropFields: true
});
